import { Fragment, type ReactNode, useEffect, useState } from 'react';

import { ThreadPrimitive } from '@assistant-ui/react';
import { Icon } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';

import { useConfig } from '@/components/providers/config-provider';
import { Button } from '@/components/ui/button';
import { DynamicIcon } from '@/components/ui/dynamic-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocalizedConfig, useLocalizedConfigWithSource } from '@/hooks/use-localized-config';
import { useRandomizedSuggestions } from '@/hooks/use-randomized-suggestions';
import { useTrackEvent } from '@/hooks/use-track-event';
import { useHistorySession } from '@/lib/history-key/session';
import { recordStarterClick } from '@/lib/starter-click-tracking';
import { useRequestStorageAccess } from '@/lib/storage/use-storage-access';
import { ChatSuggestion, WelcomeAlignment } from '@/lib/types/tenant';
import { cn } from '@/lib/utils';
import { MAX_WELCOME_SUGGESTIONS } from '@/lib/utils/welcome-starters';

const ALIGNMENT_CLASSES: Record<WelcomeAlignment, string> = {
  start: 'items-start text-start',
  center: 'items-center text-center',
  end: 'items-end text-end',
};

const VERTICAL_ALIGN_CLASSES: Record<WelcomeAlignment, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
};

// A CSS entrance, not a motion one: motion server-renders opacity 0 and only reveals the welcome once
// the page hydrates, which left it blank for seconds after the language switch reload.
const ENTRANCE_CLASSES =
  'animate-in duration-300 ease-out fill-mode-both fade-in motion-reduce:animate-none';

// Which saved list the welcome screen shows; the signed-in guest switch will set it per visitor.
const SHOWN_STARTER_AUDIENCE = 'everyone';
// A trigger override (e.g. T5's emergency starters) replaces the saved list for that opening.
const TRIGGER_STARTER_AUDIENCE = 'trigger';

const SELF_ALIGN_CLASSES: Record<WelcomeAlignment, string> = {
  start: 'self-start',
  center: 'self-center',
  end: 'self-end',
};

const SIGNED_IN_WAIT_MS = 1500;
const FIRST_NAME_PLACEHOLDER = '{firstName}';

// The name goes in after the <br> split and as plain text, so it can never become markup.
function parseLineBreaks(text: string, firstName = ''): ReactNode {
  const parts = text
    .split(/<br\s*\/?>/gi)
    .map(part => part.split(FIRST_NAME_PLACEHOLDER).join(firstName));
  if (parts.length === 1) return parts[0];

  return parts.map((part, i) => (
    <Fragment key={i}>
      {part}
      {i < parts.length - 1 && <br />}
    </Fragment>
  ));
}

/**
 * Hides the welcome until the session lookup answers, so a signed-in guest never sees the anonymous
 * one first. A lookup slower than the wait settles on the anonymous welcome for good: a late swap
 * would read as a flicker.
 */
function useWelcomeAudience(enabled: boolean) {
  const signedIn = useHistorySession(state => state.signedIn);
  const firstName = useHistorySession(state => state.firstName);
  const [gaveUp, setGaveUp] = useState(false);
  const pending = enabled && !gaveUp && signedIn === undefined;
  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(() => setGaveUp(true), SIGNED_IN_WAIT_MS);
    return () => clearTimeout(timer);
  }, [pending]);
  if (!enabled || gaveUp) return { pending: false, signedIn: false };
  return { pending, signedIn: Boolean(signedIn), firstName };
}

export function ThreadWelcome() {
  const t = useTranslations();
  const { branding, assetBasePath } = useConfig();
  const welcomeTitle = useLocalizedConfig<string>('chat.welcomeTitle');
  const welcomeSubtitle = useLocalizedConfig<string>('chat.welcomeSubtitle');
  const signedInTitle = useLocalizedConfig<string>('chat.signedIn.welcomeTitle');
  const signedInTitleWithName = useLocalizedConfig<string>('chat.signedIn.welcomeTitleWithName');
  const signedInSubtitle = useLocalizedConfig<string>('chat.signedIn.welcomeSubtitle');
  const audience = useWelcomeAudience(
    Boolean(signedInTitle || signedInTitleWithName || signedInSubtitle),
  );
  const firstName = audience.signedIn && signedInTitleWithName ? audience.firstName : undefined;
  const title =
    (firstName && signedInTitleWithName) || (audience.signedIn && signedInTitle) || welcomeTitle;
  const subtitle = (audience.signedIn && signedInSubtitle) || welcomeSubtitle;

  const welcomeConfig = branding?.components?.welcome;
  const welcomeImage = welcomeConfig?.image;
  const imageSrc =
    welcomeImage?.path && assetBasePath ? `${assetBasePath}/${welcomeImage.path}` : null;

  return (
    <div className="aui-thread-welcome-root flex w-full flex-grow flex-col">
      <div className="aui-thread-welcome-center flex w-full flex-grow flex-col items-center justify-center">
        <div
          key={audience.pending ? 'pending' : 'settled'}
          className={cn(
            'aui-thread-welcome-message flex size-full flex-col px-4 py-6',
            audience.pending && 'invisible',
            firstName && 'ph-no-capture',
            welcomeConfig?.verticalAlign
              ? VERTICAL_ALIGN_CLASSES[welcomeConfig.verticalAlign]
              : 'justify-center',
            welcomeConfig?.align && ALIGNMENT_CLASSES[welcomeConfig.align],
          )}
        >
          <h1
            className={cn(
              'aui-thread-welcome-message-motion-1 text-(length:--welcome-title-font-size,24px) font-(--welcome-title-font-weight,600)',
              ENTRANCE_CLASSES,
              'slide-in-from-bottom-[10px]',
            )}
          >
            {title ? parseLineBreaks(title, firstName) : t('thread.welcomeTitle')}
          </h1>
          <p
            className={cn(
              'aui-thread-welcome-message-motion-2 text-(length:--welcome-subtitle-font-size,24px) font-(--welcome-subtitle-font-weight,400) text-(--welcome-subtitle-color,var(--muted-foreground)/65)',
              ENTRANCE_CLASSES,
              'delay-100 slide-in-from-bottom-[10px]',
            )}
          >
            {subtitle ? parseLineBreaks(subtitle) : t('thread.welcomeSubtitle')}
          </p>
          {imageSrc && (
            <img
              src={imageSrc}
              alt={welcomeImage?.alt ?? ''}
              className={cn(
                'aui-thread-welcome-image mt-4',
                ENTRANCE_CLASSES,
                'delay-200 slide-in-from-bottom-[10px]',
                SELF_ALIGN_CLASSES[welcomeImage?.align ?? 'center'],
              )}
              style={welcomeImage?.style}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export function ThreadWelcomeSuggestions({ refreshing = false }: { refreshing?: boolean }) {
  const { value: rawSuggestions, isFromTriggerOverride } =
    useLocalizedConfigWithSource<ChatSuggestion[]>('chat.suggestions');
  const isRotating = useLocalizedConfig<boolean>('chat.randomizeSuggestions') ?? false;
  const audience = isFromTriggerOverride ? TRIGGER_STARTER_AUDIENCE : SHOWN_STARTER_AUDIENCE;
  const displayedSuggestions = useRandomizedSuggestions({
    suggestions: rawSuggestions,
    isRotating,
    audience,
  });
  const { requestOnInteraction } = useRequestStorageAccess();
  const trackEvent = useTrackEvent();
  const locale = useLocale();

  const isPicking = displayedSuggestions === null;
  // Same count the hook will show (fixed + random slots = the first MAX), as placeholders.
  const shownSuggestions =
    displayedSuggestions ?? (rawSuggestions ?? []).slice(0, MAX_WELCOME_SUGGESTIONS);
  if (!shownSuggestions.length) {
    return null;
  }

  const isShownAsPinned = (starter: ChatSuggestion) => isRotating && starter.pinned === true;
  const handleStarterClick = ({
    starter,
    position,
  }: {
    starter: ChatSuggestion;
    position: number;
  }) => {
    requestOnInteraction();
    recordStarterClick({
      trackEvent,
      starterId: starter.id,
      text: starter.title,
      position,
      isDated: starter.isDated ?? false,
      isPinned: isShownAsPinned(starter),
      isRotating,
      audience,
      language: locale,
    });
  };

  return (
    <div className="aui-thread-welcome-suggestions mt-auto grid w-full auto-rows-max gap-3 px-4 pb-4 @xl:grid-cols-2">
      {shownSuggestions.map((suggestedAction: ChatSuggestion, index: number) => {
        const { id, title, icon, iconNode } = suggestedAction;

        return (
          <div
            key={id}
            style={{ animationDelay: `${index * 50}ms` }}
            className={cn(
              'aui-thread-welcome-suggestion-display grid [&:nth-child(n+3)]:hidden @xl:[&:nth-child(n+3)]:block @xl:[&:nth-child(n+5)]:hidden @4xl:[&:nth-child(n+5)]:block',
              ENTRANCE_CLASSES,
              'slide-in-from-bottom-5',
            )}
          >
            {refreshing || isPicking ? (
              <Skeleton className="aui-thread-welcome-suggestion-skeleton h-full w-full rounded-3xl border border-border px-6 py-4">
                {isPicking && (
                  <span className="invisible block text-sm leading-relaxed font-medium">
                    {title}
                  </span>
                )}
              </Skeleton>
            ) : (
              <ThreadPrimitive.Suggestion prompt={title} send asChild>
                <Button
                  variant="ghost"
                  onClick={() =>
                    handleStarterClick({ starter: suggestedAction, position: index + 1 })
                  }
                  className="aui-thread-welcome-suggestion relative flex h-full w-full flex-col items-start justify-center gap-1 rounded-3xl border border-border px-6 py-4 text-start text-sm dark:hover:bg-accent/60"
                  aria-label={title}
                  data-starter-id={id}
                  // Read by the early-input script, for a tap made before the chat is ready; it
                  // also sets aria-disabled on the others while one waits.
                  suppressHydrationWarning
                  data-starter-position={index + 1}
                  data-starter-dated={String(suggestedAction.isDated ?? false)}
                  data-starter-pinned={String(isShownAsPinned(suggestedAction))}
                  data-starter-rotating={String(isRotating)}
                  data-starter-audience={audience}
                >
                  <span className="aui-thread-welcome-suggestion-text-1 flex w-full items-center gap-2 leading-relaxed font-medium whitespace-normal">
                    {iconNode ? (
                      <Icon iconNode={iconNode} className="size-4 shrink-0" />
                    ) : (
                      icon && <DynamicIcon name={icon} className="size-4 shrink-0" />
                    )}
                    {title}
                  </span>
                </Button>
              </ThreadPrimitive.Suggestion>
            )}
          </div>
        );
      })}
    </div>
  );
}
